import { UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { ControlProperty } from '../../../../../../common/models';
import { DayOfWeek, TIMEPICKER_DISPLAY_FORMAT } from '../../../../../../common/constants';
import { isNil } from 'lodash';
import * as moment from 'moment';
import { LocalDate, LocalDateModel } from '../../../../../models';
import { getTimestampWithTimezoneOffset, getTimezoneOffsetInHours } from '../../../../../../common/utils';
import { formatInTimeZone } from 'date-fns-tz';

export class WorkingDayModel<T = number> {
  start: T = null;
  end: T = null;

  constructor(params?: WorkingDayModel<T>) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingDayUIModel {
  start: string = null;
  end: string = null;

  constructor(params?: WorkingDayUIModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingDayForm {
  constructor(
    model: WorkingDayUIModel,
    public start: ControlProperty<string> = [model.start, []],
    public end: ControlProperty<string> = [model.end, []],
  ) {}
}

export class WorkingHoursResponseModel {
  calendars: WorkingHoursBrief[];
  timezone: string;
}

export class WorkingHoursBrief {
  id?: number;
  validFrom: number;

  constructor(params?: WorkingHoursBrief) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursViewCalendarModel {
  validFrom: LocalDateModel;
  workingHours: WorkingHoursCalendarModel[];
}

export class WorkingHoursCalendarModel {
  start: LocalDate;
  end: LocalDate;
}

export class WorkingHoursModel extends WorkingHoursBrief {
  monday: WorkingDayModel[] = [];
  tuesday: WorkingDayModel[] = [];
  wednesday: WorkingDayModel[] = [];
  thursday: WorkingDayModel[] = [];
  friday: WorkingDayModel[] = [];
  saturday: WorkingDayModel[] = [];
  sunday: WorkingDayModel[] = [];
  timezone: string = null;

  constructor(params?: WorkingHoursModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursUIModel extends WorkingHoursBrief {
  monday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  tuesday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  wednesday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  thursday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  friday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  saturday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  sunday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  timezone: string = null;

  constructor(params?: WorkingHoursUIModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursForm {
  constructor(
    model: WorkingHoursModel | WorkingHoursUIModel,
    fb: UntypedFormBuilder,
    public id: ControlProperty<number> = model.id,
    public validFrom: ControlProperty<number> = [model.validFrom, [Validators.required]],
    public timezone: ControlProperty<string> = model.timezone,
    public monday: UntypedFormArray = fb.array([]),
    public tuesday: UntypedFormArray = fb.array([]),
    public wednesday: UntypedFormArray = fb.array([]),
    public thursday: UntypedFormArray = fb.array([]),
    public friday: UntypedFormArray = fb.array([]),
    public saturday: UntypedFormArray = fb.array([]),
    public sunday: UntypedFormArray = fb.array([]),
  ) {
    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayUIModel[] = model[dayAsKey];
      dayTimes.forEach((time: WorkingDayUIModel) => {
        this[dayAsKey].push(fb.group(new WorkingDayForm(new WorkingDayUIModel(time))));
      });
    });
  }
}

export class WorkingDayModelToUIModel extends WorkingDayUIModel {
  constructor({ start, end }: WorkingDayModel, timeZone: string) {
    super();

    const safeTimeZone = typeof timeZone === 'string' && timeZone.trim().length > 0 ? timeZone : 'UTC';

    if (!isNil(start)) {
      this.start = formatInTimeZone(start, safeTimeZone, TIMEPICKER_DISPLAY_FORMAT);
    }

    if (!isNil(end)) {
      this.end = formatInTimeZone(end, safeTimeZone, TIMEPICKER_DISPLAY_FORMAT);
    }
  }
}

export class WorkingDayUIModelToTimestampModel extends WorkingDayModel {
  constructor({ start, end }: WorkingDayUIModel, validFrom: number, timezoneOffset: number) {
    super();
    this.start = start
      ? getTimestampWithTimezoneOffset(moment(validFrom).add(moment.duration(start)).valueOf(), timezoneOffset)
      : this.start;

    this.end = end
      ? getTimestampWithTimezoneOffset(moment(validFrom).add(moment.duration(end)).valueOf(), timezoneOffset)
      : this.end;
  }
}

export class WorkingHoursTimestampToFieldFormat extends WorkingHoursUIModel {
  constructor(params?: WorkingHoursModel) {
    super();
    if (!params) {
      return;
    }
    const timezoneOffset = getTimezoneOffsetInHours(params?.timezone);

    Object.assign(this, params);
    this.validFrom = getTimestampWithTimezoneOffset(params.validFrom, -timezoneOffset);
    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayModel[] = [...params[dayAsKey]];
      this[dayAsKey] = dayTimes.map((time: WorkingDayModel) => {
        const timeWithoutOffset = {
          start: time.start,
          end: time.end,
        };

        return new WorkingDayModelToUIModel(timeWithoutOffset, params?.timezone);
      });
    });
  }
}

export class WorkingHoursFieldFormatToTimestamp extends WorkingHoursModel {
  constructor(params?: WorkingHoursUIModel) {
    super();
    if (!params) {
      return;
    }
    const timezoneOffset = getTimezoneOffsetInHours(params?.timezone);
    Object.assign(this, params);
    this.validFrom = getTimestampWithTimezoneOffset(params.validFrom, timezoneOffset);
    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayUIModel[] = [...params[dayAsKey]];
      this[dayAsKey] = dayTimes.map((time: WorkingDayUIModel) => {
        return new WorkingDayUIModelToTimestampModel(time, params.validFrom, timezoneOffset);
      });
    });
  }
}
